/**
 * Cache compartido de /api/membership/plan-pricing.
 *
 * Perf mobile (PostHog replay 019f6c0a…): DualCTAVideoLanding y
 * MuxVideoPlayerWithCTA fetcheaban `plan_tier=vip` en paralelo — dos requests
 * idénticos en el arranque de la landing. Un solo fetch por tier por sesión,
 * con dedupe de in-flight (ambos consumidores comparten la misma promise).
 *
 * Best-effort: timeout interno de 2.5s; en error (rechazo de red/abort o
 * respuesta non-ok) se limpia la entrada para permitir reintento en el
 * próximo mount.
 */

export type PlanPricing = {
  amount_cents?: number;
  currency?: string;
  stripe_price_id?: string | null;
  product_name?: string | null;
};

const inflight = new Map<string, Promise<PlanPricing | null>>();

export function fetchPlanPricing(planTier: string): Promise<PlanPricing | null> {
  const tier = planTier.trim().toLowerCase();
  if (!tier) return Promise.resolve(null);
  const cached = inflight.get(tier);
  if (cached) return cached;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 2_500);
  const p = fetch(`/api/membership/plan-pricing?plan_tier=${encodeURIComponent(tier)}`, {
    signal: controller.signal,
  })
    .then((r) => {
      if (!r.ok) {
        inflight.delete(tier); // respuesta non-ok: permitir retry en el próximo mount
        return null;
      }
      return r.json() as Promise<PlanPricing>;
    })
    .catch(() => {
      inflight.delete(tier); // permitir retry en el próximo mount
      return null;
    })
    .finally(() => window.clearTimeout(timeout));
  inflight.set(tier, p);
  return p;
}
